import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface CarouselProps {
  children: ReactNode
  ariaLabel?: string
}

/**
 * Wraps a horizontally scrolling strip and overlays working chevron buttons.
 * Left chevron appears after scrolling past the first card; right chevron
 * hides once the end is reached. The inner element must be the scroll
 * container (overflow-x: auto) - Carousel grabs it via the first child ref
 * so screens can keep using their existing grid className.
 */
export function Carousel({ children, ariaLabel }: CarouselProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(true)

  useEffect(() => {
    const wrapper = scrollerRef.current
    if (!wrapper) return
    const scroller = wrapper.firstElementChild as HTMLElement | null
    if (!scroller) return

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scroller
      setCanLeft(scrollLeft > 4)
      setCanRight(scrollLeft + clientWidth < scrollWidth - 4)
    }

    update()
    scroller.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(scroller)
    return () => {
      scroller.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [])

  function scrollBy(direction: 1 | -1) {
    const wrapper = scrollerRef.current
    if (!wrapper) return
    const scroller = wrapper.firstElementChild as HTMLElement | null
    if (!scroller) return
    scroller.scrollBy({ left: scroller.clientWidth * 0.85 * direction, behavior: 'smooth' })
  }

  return (
    <div className="fq-carousel" ref={scrollerRef} aria-label={ariaLabel}>
      {children}
      {canLeft && (
        <button
          type="button"
          className="fq-carousel-chevron fq-carousel-chevron--left"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll back"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          className="fq-carousel-chevron fq-carousel-chevron--right"
          onClick={() => scrollBy(1)}
          aria-label="Scroll forward"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  )
}
